import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users, TrendingUp, AlertTriangle, Check } from 'lucide-react';
import { SelectMansione } from '../../utils/mansioni';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import { MODALITA_COMPENSO, descriviCompenso, haFisso } from '../../utils/compenso';

const SOGLIA_ESENTE = 5000;
const SOGLIA_INPS   = 15000;

const fasciaColore = (fascia) => ({
  ESENTE: { color: '#22C55E', bg: '#22C55E15', label: 'ESENTE' },
  INPS:   { color: '#F59E0B', bg: '#F59E0B15', label: 'INPS'   },
  IRPEF:  { color: '#EF4444', bg: '#EF444415', label: 'IRPEF'  },
}[fascia] || { color: '#888', bg: '#88888815', label: '—' });

const BarraFascia = ({ ytd, fascia }) => {
  const soglia = fascia === 'IRPEF' ? SOGLIA_INPS : fascia === 'INPS' ? SOGLIA_INPS : SOGLIA_ESENTE;
  const pct    = Math.min(100, (ytd / soglia) * 100);
  const col    = fasciaColore(fascia);
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize: 'var(--font-size-xs)', color:'#555', marginBottom:'3px' }}>
        <span>€{ytd.toFixed(0)} YTD</span>
        <span>soglia €{soglia.toLocaleString('it-IT')}</span>
      </div>
      <div style={{ height:'5px', background:'var(--bg-input)', borderRadius:'3px', overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', background:col.color, borderRadius:'3px', transition:'width 0.3s' }}/>
      </div>
    </div>
  );
};


export default function Dipendenti() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const puoCreare = puoFare(user, 'dipendenti', 'create');
  const anno     = new Date().getFullYear();
  const [dipendenti, setDipendenti]       = useState([]);
  const [disabilitati, setDisabilitati]   = useState([]);
  const [mostraDisab, setMostraDisab]     = useState(false);
  const [loading, setLoading]             = useState(true);
  const [showForm, setShowForm]           = useState(false);
  const [form, setForm]             = useState({
    nome:'', cognome:'', email:'', telefono:'', codice_fiscale:'',
    ruolo_descrizione:'Allenatore', ruolo_specifica:'', modalita_compenso:'ORARIO', tariffa_oraria:'', compenso_fisso:'', data_inizio:'', note:'',
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // i disabilitati non devono mai far fallire l'elenco principale
      const [r, rd] = await Promise.all([
        api.get(`/dipendenti?anno=${anno}`),
        api.get('/dipendenti/disabilitati').catch(() => ({ data: [] })),
      ]);
      setDipendenti(r.data);
      setDisabilitati(rd.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/dipendenti', {
        ...form,
        tariffa_oraria: parseFloat(form.tariffa_oraria) || 0,
        compenso_fisso: form.compenso_fisso !== '' ? parseFloat(form.compenso_fisso) : null,
        data_inizio: form.data_inizio || null,
      });
      setShowForm(false);
      setForm({ nome:'', cognome:'', email:'', telefono:'', codice_fiscale:'', ruolo_descrizione:'Allenatore', ruolo_specifica:'', modalita_compenso:'ORARIO', tariffa_oraria:'', compenso_fisso:'', data_inizio:'', note:'' });
      load();
    } catch(e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const totaleYtd    = dipendenti.reduce((s, d) => s + (d.kpi?.totale_importo || 0), 0);
  const nSegnalazioni = dipendenti.reduce((s, d) => s, 0);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'24px' }}>
        <div>
          <h1 style={s.title}>DIPENDENTI</h1>
          <p style={s.sub}>Compensi e presenze · Anno {anno}</p>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button style={s.btnSec} onClick={() => navigate('/dipendenti/calendario')}>
            <TrendingUp size={13}/> CALENDARIO
          </button>
          {disabilitati.length > 0 && (
            <button style={{ ...s.btnSec, color: mostraDisab?'#F4600C':'#777' }}
              onClick={() => setMostraDisab(v => !v)}>
              {mostraDisab ? 'Nascondi disabilitati' : `Disabilitati (${disabilitati.length})`}
            </button>
          )}
          {puoCreare && (
            <button style={s.btnPri} onClick={() => setShowForm(v => !v)}>
              <Plus size={13}/> NUOVO DIPENDENTE
            </button>
          )}
        </div>
      </div>

      {/* KPI globali */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'24px' }}>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>DIPENDENTI ATTIVI</div>
          <div style={s.kpiVal}>{dipendenti.length}</div>
        </div>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>TOTALE COMPENSI {anno}</div>
          <div style={{ ...s.kpiVal, color:'#F59E0B' }}>€{totaleYtd.toFixed(2)}</div>
        </div>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>IN FASCIA INPS/IRPEF</div>
          <div style={{ ...s.kpiVal, color:'#EF4444' }}>
            {dipendenti.filter(d => d.kpi?.fascia !== 'ESENTE').length}
          </div>
        </div>
      </div>

      {/* Form nuovo dipendente */}
      {showForm && (
        <form onSubmit={handleSubmit} style={{ background:'var(--bg-card,#141414)', border:'1px solid #F4600C40', borderRadius:'12px', padding:'20px', marginBottom:'20px' }}>
          <div style={s.formTitle}>NUOVO DIPENDENTE</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
            <div><label style={s.label}>NOME *</label><input style={s.inp} value={form.nome} onChange={e=>setForm(p=>({...p,nome:e.target.value}))} required/></div>
            <div><label style={s.label}>COGNOME *</label><input style={s.inp} value={form.cognome} onChange={e=>setForm(p=>({...p,cognome:e.target.value}))} required/></div>
            <div><label style={s.label}>EMAIL</label><input style={s.inp} type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/></div>
            <div><label style={s.label}>TELEFONO</label><input style={s.inp} value={form.telefono} onChange={e=>setForm(p=>({...p,telefono:e.target.value}))}/></div>
            <div><label style={s.label}>CODICE FISCALE</label><input style={s.inp} value={form.codice_fiscale} onChange={e=>setForm(p=>({...p,codice_fiscale:e.target.value.toUpperCase()}))} maxLength={16}/></div>
            <div>
              <label style={s.label}>MANSIONE (RIFORMA SPORT)</label>
              <SelectMansione style={s.inp} value={form.ruolo_descrizione} onChange={v=>setForm(p=>({...p,ruolo_descrizione:v}))}/>
            </div>
            <div><label style={s.label}>SPECIFICA (FACOLTATIVA)</label><input style={s.inp} placeholder="es. 2° grado beach volley" value={form.ruolo_specifica} onChange={e=>setForm(p=>({...p,ruolo_specifica:e.target.value}))}/></div>
            <div>
              <label style={s.label}>MODALITÀ COMPENSO</label>
              <div style={{ display:'flex', gap:'6px' }}>
                {MODALITA_COMPENSO.map(([v,label]) => (
                  <button key={v} type="button"
                    style={{ flex:1, padding:'8px 10px', borderRadius:'8px', border:`1px solid ${form.modalita_compenso===v?'#F4600C':'var(--border2,#2A2A2A)'}`,
                      background:form.modalita_compenso===v?'#F4600C20':'var(--bg-input,#1A1A1A)', color:form.modalita_compenso===v?'#F4600C':'#777',
                      fontWeight:'700', fontSize: 'var(--font-size-xs)', cursor:'pointer' }}
                    onClick={() => setForm(p=>({...p, modalita_compenso:v, tariffa_oraria: v==='FISSO' ? '0' : p.tariffa_oraria}))}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div><label style={s.label}>DATA INIZIO</label><input style={s.inp} type="date" value={form.data_inizio} onChange={e=>setForm(p=>({...p,data_inizio:e.target.value}))}/></div>
            {haFisso(form.modalita_compenso) && (
              <div><label style={s.label}>COMPENSO FISSO MENSILE (€) *</label><input style={s.inp} type="number" step="0.01" min="0" value={form.compenso_fisso ?? ''} onChange={e=>setForm(p=>({...p,compenso_fisso:e.target.value}))} required/></div>
            )}
            {form.modalita_compenso !== 'FISSO' && (
              <div><label style={s.label}>TARIFFA ORARIA (€) *</label><input style={s.inp} type="number" step="0.01" min={form.modalita_compenso==='FISSO_ORE' ? '0.01' : '0'} value={form.tariffa_oraria} onChange={e=>setForm(p=>({...p,tariffa_oraria:e.target.value}))} required/></div>
            )}
            {form.modalita_compenso === 'FISSO_ORE' && (
              <div style={{ gridColumn:'1/-1', fontSize:'var(--font-size-xs)', color:'var(--text-dim,#888)' }}>
                Ogni mese: fisso + tutte le ore inserite nelle presenze × tariffa oraria. Il fisso si propone da solo in Paghe mensili: apri il mese e salva.
              </div>
            )}
          </div>
          <div style={{ marginBottom:'12px' }}>
            <label style={s.label}>NOTE</label>
            <textarea style={{ ...s.inp, resize:'vertical', minHeight:'60px' }} value={form.note} onChange={e=>setForm(p=>({...p,note:e.target.value}))}/>
          </div>
          <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
            <button type="button" style={s.btnSec} onClick={()=>setShowForm(false)}>ANNULLA</button>
            <button type="submit" style={s.btnPri} disabled={saving}>{saving?'SALVO...':'SALVA'}</button>
          </div>
        </form>
      )}

      {/* Lista dipendenti */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'60px', color:'#555' }}>Caricamento...</div>
      ) : dipendenti.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px', color:'#555' }}>
          <Users size={40} color="var(--text-dim)" style={{ marginBottom:'12px' }}/>
          <p>Nessun dipendente. Usa "+ NUOVO DIPENDENTE" per iniziare.</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {/* Disabilitati */}
          {mostraDisab && disabilitati.map(d => (
            <div key={d.id}
              style={{ background:'var(--bg-card,#141414)', border:'1px solid #EF444430', borderRadius:'12px', padding:'14px 20px', cursor:'pointer', opacity:0.6 }}
              onClick={() => navigate(`/dipendenti/${d.id}`)}>
              <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                <div style={{ width:'36px', height:'36px', background:'#EF444420', border:'1px solid #EF444430', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize: 'var(--font-size-sm)', fontWeight:'800', color:'#EF4444', flexShrink:0 }}>
                  {d.nome[0]}{d.cognome[0]}
                </div>
                <div>
                  <span style={{ fontWeight:'700', fontSize: 'var(--font-size-sm)' }}>{d.cognome} {d.nome}</span>
                  <span style={{ fontSize: 'var(--font-size-xs)', color:'#EF4444', marginLeft:'8px', fontWeight:'700' }}>DISABILITATO</span>
                </div>
                <span style={{ marginLeft:'auto', fontSize: 'var(--font-size-xs)', color:'#555' }}>{d.ruolo_descrizione}</span>
              </div>
            </div>
          ))}

          {dipendenti.map(d => {
            const col   = fasciaColore(d.kpi?.fascia);
            const ytd   = d.kpi?.totale_importo || 0;
            const fascia = d.kpi?.fascia || 'ESENTE';
            return (
              <div key={d.id}
                style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'12px', padding:'16px 20px', cursor:'pointer', transition:'border-color 0.15s' }}
                onClick={() => navigate(`/dipendenti/${d.id}`)}
                onMouseEnter={e=>e.currentTarget.style.borderColor='#F4600C50'}
                onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border,#1E1E1E)'}>
                <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
                  <div style={{ width:'40px', height:'40px', background:'#F4600C20', border:'1px solid #F4600C30', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Barlow Condensed'", fontWeight:'800', color:'#F4600C', fontSize: 'var(--font-size-lg)', flexShrink:0 }}>
                    {d.nome[0]}{d.cognome[0]}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'2px' }}>
                      <span style={{ fontWeight:'700', fontSize: 'var(--font-size-sm)' }}>{d.cognome} {d.nome}</span>
                      <span style={{ fontSize: 'var(--font-size-xs)', color:'#555', fontWeight:'600' }}>{d.ruolo_descrizione}</span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>
                      {descriviCompenso(d)}
                      {d.email && <> · {d.email}</>}
                    </div>
                  </div>
                  <div style={{ minWidth:'200px' }}>
                    <BarraFascia ytd={ytd} fascia={fascia}/>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800', color:'var(--text,#fff)' }}>
                      €{ytd.toFixed(0)}
                    </div>
                    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'800', padding:'2px 7px', borderRadius:'4px', color:col.color, background:col.bg }}>
                      {col.label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  title:     { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' },
  sub:       { color:'var(--text-muted,#888)', fontSize: 'var(--font-size-sm)', marginTop:'4px' },
  btnPri:    { display:'flex', alignItems:'center', gap:'6px', background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnSec:    { display:'flex', alignItems:'center', gap:'6px', background:'var(--bg-input,#1A1A1A)', color:'var(--text,#fff)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  kpi:       { background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'12px', padding:'16px 20px' },
  kpiLabel:  { fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'6px' },
  kpiVal:    { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800' },
  formTitle: { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800', color:'#F4600C', marginBottom:'16px' },
  label:     { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'#555', display:'block', marginBottom:'4px' },
  inp:       { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', color:'var(--text,#fff)', fontSize: 'var(--font-size-sm)', outline:'none', width:'100%', boxSizing:'border-box' },
};
